import { assign } from 'xstate';
import logger from '../../../logger';
import { saveUserSettings } from '../../cloud/userSettings';
import type { AppStateContext } from '../../../types/appState';

// ---------------------------------------------------------------------------
// Service functions
// ---------------------------------------------------------------------------

async function saveSelectedNotebookService(context: AppStateContext, event: any) {
  const { selectedNotebook } = event.data;
  await saveUserSettings({
    property: 'selected_notebook',
    payload: { selected_notebook: selectedNotebook.notebook_id },
  });
  return { selected_notebook: selectedNotebook.notebook_id };
}

async function saveSelectedAOFService(context: AppStateContext, event: any) {
  const { collectionId } = event.data;
  await saveUserSettings({
    property: 'selected_area_of_focus',
    payload: { selected_area_of_focus: collectionId },
  });
  return { selected_area_of_focus: collectionId };
}

async function saveHistoryRetentionService(context: AppStateContext, event: any) {
  const { value } = event.data;
  await saveUserSettings({
    property: 'history_retention',
    payload: { history_retention: value },
  });
  return { history_retention: value };
}

// ---------------------------------------------------------------------------
// Idle event routing
// ---------------------------------------------------------------------------

export const settingsIdleOn = {
  saveSelectedNotebookUserSettings: { target: 'savingSelectedNotebookUserSettings' },
  saveSelectedAOFUserSettings: { target: 'savingSelectedAOFUserSettings' },
  saveHistoryRetentionUserSettings: { target: 'savingHistoryRetentionUserSettings' },
};

// ---------------------------------------------------------------------------
// State definitions
// ---------------------------------------------------------------------------

const savingsEntry = [(context: any, event: any) => {
  logger.debug({ context, event }, 'Saving user settings data');
}];

export const settingsStates = {
  savingSelectedNotebookUserSettings: {
    id: 'savingSelectedNotebookUserSettings',
    initial: 'savings',
    states: {
      savings: {
        entry: savingsEntry,
        invoke: {
          src: saveSelectedNotebookService,
          onDone: {
            target: '#idle',
            actions: assign<AppStateContext, any>((context, event) => {
              const updatedProperty = event.data.selected_notebook
                ? { selected_notebook: event.data.selected_notebook }
                : {};
              return { userSettings: { ...context.userSettings, ...updatedProperty } };
            }),
          },
          onError: {
            target: '#savingUserSettingsFailure',
            actions: () => false,
          },
        },
      },
    },
  },

  savingSelectedAOFUserSettings: {
    id: 'savingSelectedAOFUserSettings',
    initial: 'savings',
    states: {
      savings: {
        entry: savingsEntry,
        invoke: {
          src: saveSelectedAOFService,
          onDone: {
            target: '#idle',
            actions: assign<AppStateContext, any>((context, event) => {
              const updatedProperty = event.data.selected_area_of_focus
                ? { selected_area_of_focus: event.data.selected_area_of_focus }
                : {};
              return { userSettings: { ...context.userSettings, ...updatedProperty } };
            }),
          },
          onError: {
            target: '#savingUserSettingsFailure',
            actions: () => false,
          },
        },
      },
    },
  },

  savingHistoryRetentionUserSettings: {
    id: 'savingHistoryRetentionUserSettings',
    initial: 'savings',
    states: {
      savings: {
        entry: [(context: any, event: any) => {
          logger.debug({ context, event }, 'Saving history_retention user setting');
        }],
        invoke: {
          src: saveHistoryRetentionService,
          onDone: {
            target: '#idle',
            actions: assign<AppStateContext, any>((context, event) => ({
              userSettings: { ...context.userSettings, history_retention: event.data.history_retention },
            })),
          },
          onError: {
            target: '#savingUserSettingsFailure',
            actions: () => false,
          },
        },
      },
    },
  },

  savingUserSettingsFailure: {
    id: 'savingUserSettingsFailure',
    entry: [(context: any, event: any) => {
      logger.info({ context, event }, 'savingUserSettingsFailure data');
    }],
    always: { target: '#idle' },
  },
};
